<script setup lang="ts">
  // Vue core imports
  import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'

  import { getRecaptchaSiteKey, loadRecaptchaScript } from '../../utils/captcha.helper'

  const props = defineProps({
    id: {
      type: String,
      default: undefined,
    },
    siteKey: {
      type: String,
      default: undefined,
    },
  })

  // Emits definitions
  const emit = defineEmits(['verify', 'expired', 'error'])

  // Refs and reactive variables
  const recaptchaWidget = ref(null as number | null)
  const recaptchaElement = ref<HTMLElement | null>(null)

  const resolvedSiteKey = computed(() => props.siteKey || getRecaptchaSiteKey())

  // Lifecycle hooks
  onMounted(async () => {
    const siteKey = resolvedSiteKey.value
    if (!siteKey) return

    const grecaptcha = await loadRecaptchaScript()
    if (!grecaptcha || !recaptchaElement.value) return

    await nextTick()

    const el = recaptchaElement.value
    if (!el || recaptchaWidget.value != null) return

    const render = () => {
      recaptchaWidget.value = grecaptcha.render(el, {
        sitekey: siteKey,
        callback: (response: string) => emit('verify', response),
        'expired-callback': () => emit('expired', null),
        'error-callback': () => emit('error', null),
      })
    }

    grecaptcha.ready?.(render) ?? render()
  })

  onUnmounted(() => {
    const widgetId = recaptchaWidget.value
    if (widgetId === null) return
    if (typeof window === 'undefined' || !window.grecaptcha) return
    window.grecaptcha.reset(widgetId)
  })
</script>

<template>
  <div :id="props.id" class="recaptcha-container">
    <div ref="recaptchaElement" />
  </div>
</template>

<style scoped>
  .recaptcha-container {
    display: flex;
    justify-content: center;
  }
</style>
